import {minmax, sectionToFixedMap, portrait, cornflowerBlue} from '../shared.js';
import {createFlipbook, scrubFlipbook} from '../animations.js';

import {
  buildNarrativeTextTimeline,
  drawFrame,
  dashPaths,
  animatePages,
} from '../animations.js';

const article = document.querySelector('#chapter13');
const narrativeSection = article.querySelector('#chapter13-narrative');
const narrativeFixed = sectionToFixedMap.get(narrativeSection);
const narrativeText = narrativeFixed.querySelector('.narrative-text');
const narrativeTimeline = new TimelineLite({paused: true});

const tortureSection = article.querySelector('#chapter13-torture');
const tortureFixed = sectionToFixedMap.get(tortureSection);
const lineFlipbook = tortureFixed.querySelector('#chapter13-flipbook-line');
const techniques = tortureFixed.querySelector('#chapter13-techniques');
const techniquesHed = techniques.querySelector('h5');
const waterFlipbook = tortureFixed.querySelector('#chapter13-flipbook-waterboard');

const khalid = tortureFixed.querySelector('#chapter13-khalid');
const khalidImage = khalid.querySelector('img');
const khalidFigure = khalid.querySelector('figure');
const khalidFigureNumber = khalidFigure.querySelector('span');
const khalidFigureCaption = khalidFigure.querySelector('figcaption');
const khalidFootnote = tortureFixed.querySelector('#chapter13-footnote-khalid');
const tortureTimeline = new TimelineLite({paused: true});

const lineStart = 100;
const lineEnd = lineStart + 200;
const waterboardStart = 625;
const waterboardEnd = waterboardStart + 200;

const fbiSection = article.querySelector('#chapter13-fbi');
const fbiFixed = sectionToFixedMap.get(fbiSection);
const fbiCurtain = fbiFixed.querySelector('.blackout-curtain');
const fbi = fbiFixed.querySelector('.fbi');
const fbiConflict = fbi.querySelector('.conflict');
const fbiFootnote = fbiFixed.querySelector('.footnote');
const fbiPages = Array.from(fbiFixed.querySelectorAll('.declassified-page'));
const fbiPathHighlights = fbiFixed.querySelectorAll('figure svg path');
const fbiQuotes = Array.from(fbiFixed.querySelectorAll('.callout'));
const fbiTimeline = new TimelineLite({paused: true});
const fbiTimelinePortrait = new TimelineLite({paused: true});

/**
 * Initialize chapter
 */
export function init() {
  console.log('chapter13:init');

  buildNarrativeTextTimeline(narrativeTimeline, narrativeText, 85);

  narrativeSection.addEventListener('scrub', ({detail: {vh}}) => narrativeTimeline.seek(minmax(0, vh, narrativeTimeline.duration())));

  initTorture();
  initFBI();
}

/**
 * Setup torture section
 */
function initTorture() {
  tortureSection.addEventListener('intersection', () => {
    const create = () => {
      createFlipbook(lineFlipbook, +article.dataset.chapter);
      createFlipbook(waterFlipbook, +article.dataset.chapter);
    };
    portrait.addListener(create);
    create();
  }, {once: true});

  TweenLite.set([lineFlipbook, waterFlipbook, techniquesHed, khalid, khalidFootnote, khalidImage, khalidFigureNumber, khalidFigureCaption], {opacity: 0});
  TweenLite.set(techniques, {left: '+=100vw'});
  TweenLite.set(techniquesHed, {x: '+=100px'});

  for (const list of techniques.querySelectorAll('dl')) {
    for (const dt of list.querySelectorAll('dt')) {
      const dd = dt.nextElementSibling;
      TweenLite.set([dt, dd], {opacity: 0, x: '+=50px'});
    }
  }

  tortureTimeline.to({}, 100, {});

  tortureTimeline.set(lineFlipbook, {opacity: 1});
  tortureTimeline.set(tortureFixed, {backgroundColor: cornflowerBlue});

  tortureSection.addEventListener('scrub', ({detail: {vh}}) => {
    tortureTimeline.seek(minmax(0, vh, tortureTimeline.duration()), false);

    let percent;

    percent = (vh < lineStart) ? 0 : (vh > lineEnd) ? 1 : (vh - lineStart) / (lineEnd - lineStart);
    scrubFlipbook(lineFlipbook, percent);

    percent = (vh < waterboardStart) ? 0 : (vh > waterboardEnd) ? 1 : (vh - waterboardStart) / (waterboardEnd - waterboardStart);
    scrubFlipbook(waterFlipbook, percent);
  });

  tortureTimeline.to(techniques, 50, {left: '-=100vw'}, lineEnd);
  tortureTimeline.set(lineFlipbook, {opacity: 0});
  tortureTimeline.add([
    TweenLite.to(techniquesHed, 25, {opacity: 1}),
    TweenLite.to(techniquesHed, 25, {x: '-=100px', ease: Sine.easeOut}),
  ], '-=5');

  for (const list of techniques.querySelectorAll('dl')) {
    for (const dt of list.querySelectorAll('dt')) {
      const dd = dt.nextElementSibling;
      const highlight = dt.querySelector('.highlight');
      tortureTimeline.add([
        TweenLite.to([dt, dd], 10, {opacity: 1}, 10),
        TweenLite.to([dt, dd], 20, {x: '-=50px', ease: Sine.easeOut}),
      ], '-=20');
      tortureTimeline.to(highlight, 10, {backgroundPosition: '0% 0%'});
    }
    if (portrait.matches) {
      tortureTimeline.to(list, 25, {x: '-=50px', opacity: 0});
      tortureTimeline.to({}, 25, {});
    } else {
      tortureTimeline.to({}, 50, {});
    }
  }

  tortureTimeline.add([
    TweenLite.to(techniques, 15, {opacity: 0}),
    TweenLite.to(waterFlipbook, 15, {opacity: 1}),
  ]);

  tortureTimeline.to(waterFlipbook, 25, {opacity: 0}, waterboardEnd);

  tortureTimeline.add([
    TweenLite.set(khalid, {opacity: 1}),
    TweenLite.to(khalidFootnote, 25, {opacity: 1}),
    TweenLite.to(khalidFigureNumber, 75, {opacity: 1}),
    TweenLite.to({count: 0}, 100, {count: 183, onUpdateParams: ['{self}'], onUpdate: ({target: {count}}) => khalidFigureNumber.innerHTML = count.toFixed(0)}),
  ], waterboardEnd - 50);

  tortureTimeline.add([
    TweenLite.to(khalidImage, 50, {opacity: 1}),
    TweenLite.to(khalidFigureCaption, 50, {opacity: 1}),
  ], '-=50');
}


/**
 * Setup the FBI section
 */
function initFBI() {
  TweenLite.set([fbiConflict, fbiFootnote, fbiQuotes, ...fbiPages], {opacity: 0});
  TweenLite.set([fbiConflict], {x: '-=25%'});
  TweenLite.set(fbiPages, {scale: 1.5});

  dashPaths(fbiPathHighlights);

  fbiTimeline.to({}, 100, {});

  fbiTimeline.to(fbiCurtain, 50, {left: '50%'});
  fbiTimeline.to(fbiConflict, 25, {opacity: 1, x: '0%'});

  fbiTimeline.to({}, 25, {});

  fbiTimeline.add([
    TweenLite.to(fbiCurtain, 50, {left: '100%'}),
    TweenLite.to(fbiConflict, 25, {opacity: 0, x: '+=25%'}),
  ]);

  fbiTimeline.to(fbiFootnote, 25, {opacity: 1});

  animatePages(fbiPages, fbiTimeline);

  fbiTimeline.to(fbiQuotes, 25, {opacity: 1});

  fbiTimelinePortrait.to({}, 100, {});

  fbiTimelinePortrait.to(fbiCurtain, 50, {left: '100%'});
  fbiTimelinePortrait.to(fbiConflict, 25, {opacity: 1, x: '0%'});

  fbiTimelinePortrait.to({}, 25, {});

  fbiTimelinePortrait.add([
    TweenLite.to(fbiConflict, 25, {opacity: 0, x: '+=25%'}),
  ]);

  fbiTimelinePortrait.to(fbiFootnote, 25, {opacity: 1});

  animatePages(fbiPages, fbiTimelinePortrait, false);

  fbiTimelinePortrait.to(fbiQuotes, 25, {opacity: 1});

  fbiSection.addEventListener('scrub', ({detail: {vh}}) => {
    if (portrait.matches) {
      fbiTimelinePortrait.seek(minmax(0, vh, fbiTimelinePortrait.duration()), false);
    } else {
      fbiTimeline.seek(minmax(0, vh, fbiTimeline.duration()), false);
    }
  });
}
